import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, allArticles } from '@/mocks/data';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSlug = searchParams.get('cat') || 'hot';
  const [activeCategory, setActiveCategory] = useState(activeSlug);

  useEffect(() => {
    setActiveCategory(activeSlug);
  }, [activeSlug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const filteredArticles = activeCategory === 'hot'
    ? allArticles
    : allArticles.filter((a) => a.category === activeCategory);

  const switchCategory = (slug: string) => {
    setSearchParams({ cat: slug });
  };

  return (
    <Layout>
      <SEOHead
        title={`${currentCategory.name} - 蜜桃在线分类浏览`}
        description={`蜜桃在线${currentCategory.name}频道，${currentCategory.description}。蜜桃在线提供极速稳定的访问体验与丰富多样的内容资源。`}
        keywords={`蜜桃在线,${currentCategory.name},蜜桃在线官网,蜜桃在线入口,在线娱乐`}
        canonicalUrl={`https://www.mitaozaixian.com/category?cat=${activeCategory}`}
      />

      <div className="pt-20">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '分类浏览', path: '/category' },
              { label: currentCategory.name },
            ]}
          />
        </div>

        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                <strong>{currentCategory.name}</strong>
              </h1>
              <p className="text-sm text-foreground-600">{currentCategory.description}</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="lg:w-60 shrink-0">
                <nav className="sticky top-24 space-y-1">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => switchCategory(cat.slug)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-left cursor-pointer whitespace-nowrap ${
                        activeCategory === cat.slug
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                      }`}
                    >
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                        activeCategory === cat.slug ? 'bg-primary-200' : 'bg-background-100'
                      }`}>
                        <i className={`${cat.icon} ${
                          activeCategory === cat.slug ? 'text-primary-600' : 'text-foreground-500'
                        }`}></i>
                      </span>
                      {cat.name}
                      {activeCategory === cat.slug && (
                        <i className="ri-check-line ml-auto text-primary-600"></i>
                      )}
                    </button>
                  ))}
                </nav>
              </aside>

              <div className="flex-1 min-w-0">
                {filteredArticles.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {filteredArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.id}`}
                        className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                      >
                        <div className="relative h-44 overflow-hidden">
                          <img
                            src={article.image}
                            alt={article.title}
                            title={`${article.title} - 蜜桃在线`}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                              {article.categoryName}
                            </span>
                            <span className="text-xs text-foreground-400">{article.date}</span>
                          </div>
                          <h3 className="font-heading text-sm text-foreground-900 mb-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {article.title}
                          </h3>
                          <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                            {article.excerpt}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-500 text-sm">该分类暂无内容</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}